import React from 'react';

import { MAX_SKILL_PROMPT_LENGTH } from '../services/landingSkillRouting';
import { isIOSApp } from '../utils/platformDetection';

import { ChatBoxInput } from './ChatBox';
import { FeatureSections } from './FeatureSections';
import { HumanAiSection } from './HumanAiSection';
import { LandingModeTabs } from './LandingModeTabs';
import { LandingQuickNav } from './LandingQuickNav';
import {
  landingChoiceAllowsMedia,
  landingPlaceholder,
  LandingSkillChoice,
} from './landingChoices';
import { LandingSkillSelector } from './LandingSkillSelector';
import { MoatSections } from './MoatSections';
import { Navbar } from './Navbar';
import { PersonaSection } from './PersonaSection';
import { PlatformProof } from './PlatformProof';
import { ProcessSection } from './ProcessSection';
import { ProjectList } from './ProjectList';
import { TrustSections } from './TrustSections';

/* The marketing sections below the fold are static: no props, no state, no
   effects. LandingPage re-renders on every keystroke in the chat input, so
   without this the full ~1,500 lines of section JSX were re-rendered per
   character typed. Memoising the block collapses that to nothing. */
const LandingSections: React.FC = React.memo(() => (
  <>
    <PlatformProof />
    <PersonaSection />
    <FeatureSections />
    <ProcessSection />
    <HumanAiSection />
    <MoatSections />
    <TrustSections />
  </>
));

LandingSections.displayName = 'LandingSections';

interface LandingPageLayoutProps {
  inputValue: string;
  onInputChange: (value: string) => void;
  onSendClick: (text: string, mediaUrls?: string[]) => Promise<void>;
  isSubmitting: boolean;
  submitError: string | null;
  isAuthenticated: boolean;
  authIsLoading: boolean;
  onConnectGitHub: () => void;
  selectedSkill: LandingSkillChoice;
  onSkillChange: (choice: LandingSkillChoice) => void;
}

/* No success banner: a successful send navigates straight to the planning page,
   so the only feedback worth rendering here is a failure. */
const Feedback: React.FC<{ error: string | null }> = ({ error }) => (
  <>
    {error && <div className="feedback error">{error}</div>}
  </>
);

const LandingActions: React.FC<{
  authIsLoading: boolean;
  onConnectGitHub: () => void;
}> = ({ authIsLoading, onConnectGitHub }) => (
  <div className="landing-actions" style={authIsLoading ? { visibility: 'hidden' } : undefined}>
    <button type="button" className="landing-github-connect" onClick={onConnectGitHub} style={{ display: 'none' }}>
      <i className="fa-brands fa-github" />
      <span>Connect existing GitHub project</span>
    </button>
  </div>
);

const LandingCard: React.FC<{
  inputValue: string;
  onInputChange: (value: string) => void;
  onSendClick: (text: string, mediaUrls?: string[]) => Promise<void>;
  isSubmitting: boolean;
  submitError: string | null;
  onConnectGitHub: () => void;
  isAuthenticated: boolean;
  authIsLoading: boolean;
  selectedSkill: LandingSkillChoice;
  onSkillChange: (choice: LandingSkillChoice) => void;
  hideActions?: boolean;
}> = ({
  inputValue, onInputChange, onSendClick, isSubmitting, submitError,
  onConnectGitHub, isAuthenticated, authIsLoading, selectedSkill, onSkillChange, hideActions,
}) => {
  const showActions = !hideActions && (isAuthenticated || authIsLoading);

  return (
    <div className={`landing-card${showActions ? '' : ' landing-card--no-actions'}`}>
      <LandingModeTabs value={selectedSkill} onChange={onSkillChange} />
      <ChatBoxInput
        key={selectedSkill.kind}
        value={inputValue}
        onChange={onInputChange}
        onSend={onSendClick}
        isLoading={isSubmitting}
        projectId={null}
        isAnyAgentRunning={false}
        hasTodoTasks={false}
        isByokPlan={false}
        userMessageCount={0}
        allowMedia={landingChoiceAllowsMedia(selectedSkill)}
        maxInputLength={selectedSkill.kind === 'skill' ? MAX_SKILL_PROMPT_LENGTH : undefined}
        placeholder={landingPlaceholder(selectedSkill)}
      />
      {showActions && <LandingActions authIsLoading={authIsLoading} onConnectGitHub={onConnectGitHub} />}
      <Feedback error={submitError} />
    </div>
  );
};


const LandingMain: React.FC<{
  inputValue: string;
  onInputChange: (value: string) => void;
  onSendClick: (text: string) => Promise<void>;
  isSubmitting: boolean;
  submitError: string | null;
  isAuthenticated: boolean;
  authIsLoading: boolean;
  onConnectGitHub: () => void;
  selectedSkill: LandingSkillChoice;
  onSkillChange: (choice: LandingSkillChoice) => void;
}> = (props) => {
  const iosApp = isIOSApp();
  const heroPrefix = iosApp
    ? <>Plan with <span className="hero__highlight">AI Agents</span> to build</>
    : undefined;

  return (
    <main className="landing-main">
      <div id="home" className="landing-above-fold">
        <div className="landing-inner">
          <LandingSkillSelector prefix={heroPrefix} />
          <LandingCard
            inputValue={props.inputValue}
            onInputChange={props.onInputChange}
            onSendClick={props.onSendClick}
            isSubmitting={props.isSubmitting}
            submitError={props.submitError}
            onConnectGitHub={props.onConnectGitHub}
            isAuthenticated={props.isAuthenticated}
            authIsLoading={props.authIsLoading}
            selectedSkill={props.selectedSkill}
            onSkillChange={props.onSkillChange}
            hideActions={iosApp}
          />
        </div>
        <div id="projects">
          <ProjectList isAuthenticated={props.isAuthenticated} authIsLoading={props.authIsLoading} />
        </div>
      </div>
      {!iosApp && <LandingSections />}
    </main>
  );
};

export const LandingPageLayout: React.FC<LandingPageLayoutProps> = (props) => (
  <div className="landing-page">
    <Navbar authIsLoading={props.authIsLoading} />
    {!isIOSApp() && <LandingQuickNav isAuthenticated={props.isAuthenticated} />}
    <LandingMain
      inputValue={props.inputValue}
      onInputChange={props.onInputChange}
      onSendClick={props.onSendClick}
      isSubmitting={props.isSubmitting}
      submitError={props.submitError}
      isAuthenticated={props.isAuthenticated}
      authIsLoading={props.authIsLoading}
      onConnectGitHub={props.onConnectGitHub}
      selectedSkill={props.selectedSkill}
      onSkillChange={props.onSkillChange}
    />
  </div>
);
